import { useRouter } from 'next/router';
import { Box, MobileStepper, Stepper, Step, StepLabel, Typography } from '@mui/material';
import { Logo } from '@care/react-component-lib';
import useFlowHelper from '@/components/hooks/useFlowHelper';
import useResponsive from '@/components/hooks/useResponsive';
import { PROGRESS_TRACKER_CONFIG, SEEKER_ROUTES } from '@/constants';
import { SxClassProps } from '@/types/common';
import { PROGRESS_TRACKER_SECTIONS } from '@/types/flow';

const classes: SxClassProps = {
  mainContainer: (theme) => ({
    display: 'flex',
    height: { xs: theme.spacing(7), md: theme.spacing(13) },
    position: 'relative',
  }),
  trackerContainer: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    px: { xs: theme.spacing(1), sm: theme.spacing(4) },
  }),
  stepLabel: (theme) => ({
    paddingRight: {
      xs: theme.spacing(1),
      sm: theme.spacing(3),
    },
    textWrap: 'nowrap',
    '& svg': {
      width: {
        xs: '20px',
        sm: '24px',
      },
    },
    '& .MuiStepLabel-root': {
      paddingRight: 0,
    },
    '& .MuiStepIcon-root': {
      color: theme.palette.care?.grey[300],
      '&.Mui-active': {
        color: '#3B71AA',
      },
    },
    '& .MuiStepLabel-iconContainer': {
      paddingRight: {
        xs: 0,
        sm: 0,
        md: theme.spacing(1),
      },
    },
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
  }),
  logo: {
    width: {
      xs: '100%',
      sm: '100%',
    },
    height: {
      xs: '22px',
      sm: '40px',
    },
  },
};

/**
 * The Progress Flow component is used to display a progress bar and a label.
 */
const ProgressTracker = () => {
  const { pathname } = useRouter();
  const { isMobile } = useResponsive();
  const { currentFlow, stepNumber, totalSteps } = useFlowHelper();

  let activeSectionIndex = -1;
  if (currentFlow && pathname) {
    activeSectionIndex = PROGRESS_TRACKER_CONFIG?.[currentFlow]?.[pathname] ?? -1;
  }
  const showTracker = activeSectionIndex >= 0;
  const setProgressAsZero = pathname === SEEKER_ROUTES.INDEX;

  return (
    <Box sx={classes.mainContainer}>
      <Box display="flex" flexDirection="row" width="100%">
        <Box sx={classes.trackerContainer}>
          <Logo sx={classes.logo} />
        </Box>
        <Box display="flex" justifyContent="flex-end" flex={1}>
          {showTracker && (
            <Stepper nonLinear activeStep={activeSectionIndex}>
              {PROGRESS_TRACKER_SECTIONS.map(({ id, name }, i) => (
                <Step
                  key={id}
                  data-testid={activeSectionIndex === i ? 'active-step' : 'inactive-step'}>
                  <StepLabel sx={classes.stepLabel}>
                    <Typography variant={isMobile ? 'info1' : 'body3'}>{name}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
        </Box>
      </Box>

      <Box position="absolute" bottom={0} width="100%">
        <MobileStepper
          variant="progress"
          steps={totalSteps + 1}
          position="static"
          activeStep={setProgressAsZero ? 0 : stepNumber}
          nextButton={null}
          backButton={null}
          className="fullWidth"
        />
      </Box>
    </Box>
  );
};

ProgressTracker.defaultProps = {
  currentFlow: '',
};

export default ProgressTracker;
